import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { validateResetToken, setPassword } from '../services/api'
import { errorText } from '../utils/errorText'
import { DEFAULT_PASSWORD_POLICY, PasswordPolicy, passwordRefusal, unmetRules } from '../utils/passwordPolicy'
import PasswordRules from '../components/PasswordRules'

export default function SetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPasswordValue] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [email, setEmail] = useState<string | null>(null)
  // The organization's rules, read with the token so they are on screen before anything is typed.
  const [policy, setPolicy] = useState<PasswordPolicy>(DEFAULT_PASSWORD_POLICY)

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No reset token provided')
        setIsValidating(false)
        return
      }

      try {
        const response = await validateResetToken(token)
        if (response.valid) {
          setTokenValid(true)
          setEmail(response.email || null)
          if (response.password_policy) setPolicy(response.password_policy)
        } else {
          setError('This link is invalid or has expired. Please contact your administrator for a new setup link.')
        }
      } catch (err: any) {
        setError('Failed to validate reset token. Please try again.')
      } finally {
        setIsValidating(false)
      }
    }

    validateToken()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const unmet = unmetRules(password, policy)
    if (unmet.length) {
      setError(passwordRefusal(unmet))
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)
    try {
      await setPassword(token!, password)
      setSuccess(true)
    } catch (err: any) {
      setError(errorText(err, 'Failed to set password. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-theme-text-secondary">Validating link...</span>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
            <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
          </div>

          <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                Password set successfully!
              </p>
            </div>
            <p className="text-sm text-green-600 dark:text-green-400 mt-2">
              You can now sign in with your email and new password.
            </p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
            <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
          </div>

          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full py-2 px-4 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
          <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-2">Set Your Password</h3>
          {email && (
            <p className="text-sm text-theme-text-secondary">
              Setting password for: <span className="font-medium text-theme-text-primary">{email}</span>
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">
              New Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter your password"
              autoComplete="new-password"
            />
            <PasswordRules password={password} policy={policy} />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="Confirm your password"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Setting password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
